/**
 * Conditional Logic Builder
 * For building question conditional logic (show/hide based on other questions)
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import type { Question } from '../../utils/api/questionBankApi';

interface ConditionalLogicBuilderProps {
  conditionalOn?: Question['conditionalOn'];
  onChange: (conditional?: Question['conditionalOn']) => void;
  availableQuestions: Array<{ id: string; text: string }>;
}

export function ConditionalLogicBuilder({
  conditionalOn,
  onChange,
  availableQuestions,
}: ConditionalLogicBuilderProps) {
  const hasConditional = !!conditionalOn;

  const updateConditional = <K extends keyof NonNullable<Question['conditionalOn']>>(
    field: K,
    value: NonNullable<Question['conditionalOn']>[K]
  ) => {
    onChange({ ...conditionalOn, questionId: conditionalOn?.questionId || '', [field]: value } as Question['conditionalOn']);
  };

  const removeConditional = () => {
    onChange(undefined);
  };

  const addConditional = () => {
    onChange({
      questionId: '',
      operator: 'equals',
      value: '',
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label>Conditional Logic</Label>
          <p className="text-sm text-muted-foreground">
            Show this question only when certain conditions are met
          </p>
        </div>
        {!hasConditional && (
          <Button type="button" variant="outline" size="sm" onClick={addConditional}>
            Add Condition
          </Button>
        )}
      </div>

      {hasConditional && (
        <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
          <div className="flex items-start justify-between">
            <p className="text-sm font-medium">Show this question when:</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={removeConditional}
              className="h-6 w-6 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-3">
            {/* Question ID */}
            <div className="space-y-2">
              <Label htmlFor="conditionalQuestion" className="text-sm">
                Question
              </Label>
              {availableQuestions.length > 0 ? (
                <Select
                  value={conditionalOn.questionId}
                  onValueChange={(value) => updateConditional('questionId', value)}
                >
                  <SelectTrigger id="conditionalQuestion">
                    <SelectValue placeholder="Select a question..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableQuestions.map((q) => (
                      <SelectItem key={q.id} value={q.id}>
                        {q.id}: {q.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="conditionalQuestion"
                  value={conditionalOn.questionId}
                  onChange={(e) => updateConditional('questionId', e.target.value)}
                  placeholder="Enter question ID (e.g., sr_types)"
                />
              )}
            </div>

            {/* Operator */}
            <div className="space-y-2">
              <Label htmlFor="conditionalOperator" className="text-sm">
                Operator
              </Label>
              <Select
                value={conditionalOn.operator || 'equals'}
                onValueChange={(value) => 
                  updateConditional('operator', value as 'equals' | 'contains' | 'greaterThan')
                }
              >
                <SelectTrigger id="conditionalOperator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equals">Equals</SelectItem>
                  <SelectItem value="contains">Contains</SelectItem>
                  <SelectItem value="greaterThan">Greater Than</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Value */}
            <div className="space-y-2">
              <Label htmlFor="conditionalValue" className="text-sm">
                Value
              </Label>
              <Input
                id="conditionalValue"
                value={conditionalOn.value || ''}
                onChange={(e) => updateConditional('value', e.target.value)}
                placeholder="Enter value to compare against"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="bg-background p-3 rounded border text-sm">
            <div className="font-mono text-xs text-muted-foreground mb-1">Preview:</div>
            <div className="font-medium">
              {conditionalOn.questionId ? (
                <>
                  Show when <code className="bg-muted px-1">{conditionalOn.questionId}</code>
                  {' '}{conditionalOn.operator || 'equals'}{' '}
                  <code className="bg-muted px-1">{conditionalOn.value || '(value)'}</code>
                </>
              ) : (
                <span className="text-muted-foreground">Configure condition above</span>
              )}
            </div>
          </div>
        </div>
      )}

      {!hasConditional && (
        <div className="text-sm text-muted-foreground p-4 border rounded-lg bg-muted/10">
          No conditional logic configured. This question will always be visible.
        </div>
      )}
    </div>
  );
}
